import { useState, useEffect, useCallback } from "react";
import { flushSync } from "react-dom";
import { useDispatch } from "react-redux";
import { getInstance, getTenants } from "../../../../store/instanceManagement/instanceService";
import { saveInstances } from "../../../../store/slices/instancesSlice";
import { saveTenantsList } from "../../../../store/slices/tenantsSlice";
import { setInstanceCultureName, setInstanceTimezone } from "../../../../store/slices/authSlice";
import { authService } from "../../../../utils/authService";
import { logger } from "../../../../logger-init";

interface SkyPointLinkConfiguration {
  DocumentLink?: string;
  IdeaLink?: string;
  PrivacyStatementLink?: string;
  [key: string]: string | undefined;
}

interface HeaderDataState {
  selectedInstanceId: string;
  selectedInstanceName: string;
  selectedTenantBusinessName: string;
  skyPointLinkConfiguration: SkyPointLinkConfiguration;
  isInstanceLoaded: boolean;
  isTenantLoaded: boolean;
}

interface UseHeaderDataReturn extends HeaderDataState {
  refreshTenantData: () => void;
  refreshInstanceData: () => void;
}

export function useHeaderData(): UseHeaderDataReturn {
  const dispatch = useDispatch();
  const [selectedInstanceId, setSelectedInstanceId] = useState<string>("");
  const [selectedInstanceName, setSelectedInstanceName] = useState<string>("");
  const [selectedTenantBusinessName, setSelectedTenantBusinessName] = useState<string>("");
  const [skyPointLinkConfiguration, setSkyPointLinkConfiguration] =
    useState<SkyPointLinkConfiguration>({});
  const [isInstanceLoaded, setIsInstanceLoaded] = useState<boolean>(false);
  const [isTenantLoaded, setIsTenantLoaded] = useState<boolean>(false);

  const fetchTenantData = useCallback(() => {
    getTenants()
      .then((data) => {
        if (!data?.tenants || data.tenants.length === 0) {
          dispatch(saveTenantsList(data));
          setIsTenantLoaded(true);
          return;
        }

        data.tenants.sort((a: { businessname: string }, b: { businessname: string }) =>
          a.businessname.toLowerCase().localeCompare(b.businessname.toLowerCase()),
        );

        // Check sessionStorage for saved tenant ID and use it if valid
        const savedTenantId = authService.getSavedTenantId();

        if (savedTenantId && data.tenants) {
          const savedTenant = data.tenants.find(
            (t: { id: string; businessname: string }) => t.id === savedTenantId,
          );
          if (savedTenant) {
            // Override API's selectedTenant with user's saved preference
            data.selectedTenant = {
              id: savedTenant.id,
              name: savedTenant.businessname,
            };
          }
        }

        dispatch(saveTenantsList(data));
        setIsTenantLoaded(true);
      })
      .catch((error) => {
        logger.error("Failed to get tenants", error instanceof Error ? error : undefined);
        setIsTenantLoaded(true);
      });
  }, [dispatch]);

  const fetchInstanceData = useCallback(() => {
    getInstance()
      .then((data) => {
        dispatch(setInstanceCultureName(data?.instanceCultureName ?? "en-US"));
        dispatch(setInstanceTimezone(data?.instanceDefault?.timezone ?? "US/Pacific"));

        if (!data?.instances || data.instances.length === 0) {
          dispatch(saveInstances({ ...data, instances: data?.instances || [] }));
          setIsInstanceLoaded(true);
          return;
        }

        data.instances.sort((a: { displayname: string }, b: { displayname: string }) =>
          a.displayname.toLowerCase().localeCompare(b.displayname.toLowerCase()),
        );

        // Check sessionStorage for saved instance ID preference
        const savedInstanceId = authService.getSavedInstanceId();
        const backendTenantId = data.tenant?.id || "";

        let finalInstanceId = data.selectedInstanceId;
        let finalInstanceName = data.selectedInstanceName;

        if (savedInstanceId && data.instances) {
          // Match the saved instance in the fetched list
          const savedInstance = data.instances.find(
            (inst: { id: string; displayname: string }) => inst.id === savedInstanceId,
          );
          if (savedInstance) {
            finalInstanceId = savedInstance.id;
            finalInstanceName = savedInstance.displayname;
          }
        }

        // Update data with the resolved instance before saving to Redux
        data.selectedInstanceId = finalInstanceId;
        data.selectedInstanceName = finalInstanceName;

        dispatch(saveInstances(data));
        authService.saveInstanceId(finalInstanceId);
        authService.saveTenantId(backendTenantId);

        flushSync(() => {
          setSelectedInstanceId(finalInstanceId);
          setSelectedInstanceName(finalInstanceName);
          setSelectedTenantBusinessName(data.tenant?.businessname || "");
          setSkyPointLinkConfiguration(data.skyPointLinkConfiguration || {});
          setIsInstanceLoaded(true);
        });

        // Store for analytics
        const instanceData = data?.instances.filter(
          (item: { id: string; name: string }) => item.id === finalInstanceId,
        );
        const instanceName = instanceData[0]?.name;
        const tenantName = data?.tenant?.name;
        if (tenantName && instanceName) {
          sessionStorage.setItem("Tenant_instance", `${tenantName.toLowerCase()}_${instanceName}`);
        }
      })
      .catch((error) => {
        logger.error("Failed to get instances", error instanceof Error ? error : undefined);
        setIsInstanceLoaded(true);
      });
  }, [dispatch]);

  useEffect(() => {
    fetchTenantData();
    fetchInstanceData();
  }, [fetchTenantData, fetchInstanceData]);

  return {
    selectedInstanceId,
    selectedInstanceName,
    selectedTenantBusinessName,
    skyPointLinkConfiguration,
    isInstanceLoaded,
    isTenantLoaded,
    refreshTenantData: fetchTenantData,
    refreshInstanceData: fetchInstanceData,
  };
}
